import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemePreference = 'light' | 'dark' | 'system';

interface UiState {
  theme: ThemePreference;
  sidebarCollapsed: boolean;
  /** Cliente selecionado no topo (escopo de trabalho). null = todos. */
  selectedClientId: string | null;
  setTheme: (theme: ThemePreference) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSelectedClientId: (clientId: string | null) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: 'system',
      sidebarCollapsed: false,
      selectedClientId: null,
      setTheme: (theme) => set({ theme }),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setSelectedClientId: (selectedClientId) => set({ selectedClientId }),
    }),
    { name: 'marketing-os.ui' },
  ),
);

function resolveDark(theme: ThemePreference): boolean {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function applyTheme(theme: ThemePreference): void {
  document.documentElement.classList.toggle('dark', resolveDark(theme));
}

/** Mantém a classe `dark` do <html> sincronizada com a preferência. */
export function useThemeEffect(): void {
  const theme = useUiStore((s) => s.theme);

  useEffect(() => {
    applyTheme(theme);
    if (theme !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => applyTheme('system');
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [theme]);
}

/** Indica se o tema resolvido no momento é escuro (para os gráficos). */
export function useIsDarkTheme(): boolean {
  const theme = useUiStore((s) => s.theme);
  return typeof window === 'undefined' ? false : resolveDark(theme);
}

export function useSelectedClient() {
  const selectedClientId = useUiStore((s) => s.selectedClientId);
  const setSelectedClientId = useUiStore((s) => s.setSelectedClientId);
  return { selectedClientId, setSelectedClientId };
}
